import { Sparkles, User } from 'lucide-react';

import type { ChatMessage } from '../hooks/use-chat.js';
import { cn } from '../lib/cn.js';

import { MarkdownRenderer } from './markdown-renderer.js';

export interface ChatMessageBubbleProps {
  readonly message: ChatMessage;
  readonly onCitationClick?: (label: string, name: string) => void;
}

export function ChatMessageBubble({
  message,
  onCitationClick,
}: ChatMessageBubbleProps): JSX.Element {
  if (message.role === 'user') return <UserBubble content={message.content} />;
  return (
    <AssistantBubble
      content={message.content}
      streaming={message.streaming ?? false}
      error={message.error}
      onCitationClick={onCitationClick}
    />
  );
}

function UserBubble({ content }: { readonly content: string }): JSX.Element {
  return (
    <div className="flex justify-end gap-2 py-2">
      <div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-gradient-to-br from-accent to-accent-dim px-3.5 py-2 text-sm text-white shadow-glow-soft">
        <p className="whitespace-pre-wrap break-words">{content}</p>
      </div>
      <div className="grid h-7 w-7 flex-none place-items-center rounded-full border border-accent/40 bg-accent/15 text-accent">
        <User className="h-3.5 w-3.5" />
      </div>
    </div>
  );
}

function AssistantBubble({
  content,
  streaming,
  error,
  onCitationClick,
}: {
  readonly content: string;
  readonly streaming: boolean;
  readonly error?: string;
  readonly onCitationClick?: (label: string, name: string) => void;
}): JSX.Element {
  return (
    <div className="flex gap-2 py-2">
      <div
        className={cn(
          'grid h-7 w-7 flex-none place-items-center rounded-full bg-gradient-to-br from-accent via-fuchsia-500 to-rose-500 text-white shadow-glow-soft',
          streaming && 'animate-pulse',
        )}
      >
        <Sparkles className="h-3.5 w-3.5" />
      </div>
      <div
        className={cn(
          'max-w-[88%] rounded-2xl rounded-tl-sm border bg-surface/70 px-3.5 py-2.5 backdrop-blur-sm',
          error
            ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
            : 'border-border-subtle text-text-primary',
        )}
      >
        {error ? (
          <div className="text-sm">Error: {error}</div>
        ) : content || streaming ? (
          <>
            <MarkdownRenderer content={content} onCitationClick={onCitationClick} />
            {streaming && (
              <span
                className="ml-0.5 inline-block h-3.5 w-1.5 translate-y-0.5 animate-pulse bg-accent"
                aria-hidden
              />
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
